export type Choice = 'RUN' | 'DIE';
export type LegacyChoice = 'SURVIVES' | 'DIES';
export type ChoiceFormat = 'run-die' | 'legacy';

export function isChoice(value: unknown): value is Choice {
  return value === 'RUN' || value === 'DIE';
}

/** Decode historical records and requests only at a compatibility boundary. */
export function normalizeChoice(value: unknown): Choice | null {
  if (value === 'RUN' || value === 'SURVIVES') return 'RUN';
  if (value === 'DIE' || value === 'DIES') return 'DIE';
  return null;
}

const choiceFields = new Set(['choice', 'outcome', 'result', 'side', 'type']);

function convertPayload(
  value: unknown,
  format: ChoiceFormat,
  field = '',
): unknown {
  if (choiceFields.has(field)) {
    const choice = normalizeChoice(value);
    if (choice)
      return format === 'run-die'
        ? choice
        : choice === 'RUN'
          ? 'SURVIVES'
          : 'DIES';
  }
  if (Array.isArray(value))
    return value.map((item) => convertPayload(item, format));
  if (!value || typeof value !== 'object') return value;

  const entries = Object.entries(value);
  if (field === 'coefficients') {
    const coefficients = value as Record<string, unknown>;
    const converted = entries.filter(([key]) => normalizeChoice(key) === null);
    for (const side of ['RUN', 'DIE'] as const) {
      const legacy = side === 'RUN' ? 'SURVIVES' : 'DIES';
      const source = Object.hasOwn(coefficients, side) ? side : legacy;
      if (Object.hasOwn(coefficients, source))
        converted.push([
          format === 'run-die' ? side : legacy,
          coefficients[source],
        ]);
    }
    return Object.fromEntries(converted);
  }
  return Object.fromEntries(
    entries.map(([key, child]) => [key, convertPayload(child, format, key)]),
  );
}

/** For known round/quote transport payloads, never for arbitrary user documents. */
export function normalizeChoicePayload<T>(value: T): T {
  return convertPayload(value, 'run-die') as T;
}

/** Wire compatibility for tabs opened before the RUN/DIE API opt-in. */
export function legacyChoicePayload<T>(value: T): T {
  return convertPayload(value, 'legacy') as T;
}
